import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DrilldownFilterConfig, DrilldownTab } from '../lib/api';

export interface DrilldownFilters {
  supplier?: string | number;
  customer?: string | number;
  site?: string | number;
  date_from?: string;
  date_to?: string;
  date_range?: string;
  status?: string;
  inspection_status?: string;
  delivery_status?: string;
  disposition?: string;
  po_number?: string;
  part_number?: string;
  otif_status?: string;
  otd_status?: string;
  po_line_id?: number;
  [key: string]: string | number | undefined;
}

export interface DrilldownField {
  field: string;
  label: string;
}

export interface DrilldownRecord {
  id: number;
  [key: string]: unknown;
}

/**
 * CR-7261: the chart figure the user clicked, carried into the drill-down so
 * the headline travels with them and can be reconciled against the records.
 * `value` is already formatted by the platform formatter at the click site,
 * so the drill-down and the tooltip can never disagree about rounding.
 */
export interface DrilldownHeadline {
  label: string;
  value: string;
}

export interface DrilldownState {
  isOpen: boolean;
  metric: string | null;
  displayName: string | null;
  description: string | null;
  filters: DrilldownFilters;
  filterConfig: DrilldownFilterConfig[];
  records: DrilldownRecord[];
  fields: DrilldownField[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  groupBy: string | null;
  groupings: string[];
  breakdown: unknown[] | null;
  // Context passed from trigger (e.g., supplier name, time period)
  contextLabel: string | null;
  // CR-7261: the clicked chart figure, shown beside the context label.
  headline: DrilldownHeadline | null;
  // Tab support
  supportsTabs: boolean;
  tabs: DrilldownTab[];
  activeTab: string | null;
  tabCounts: { [key: string]: number };
  // CR-4791: optional tab key the caller wants the drill-down to open on.
  // When set and the loaded config has a matching tab, setMetricConfig uses
  // this instead of defaulting to the first tab — lets pie-slice clicks
  // land on the tab that matches what the user clicked.
  pendingInitialTab: string | null;
}

interface DrilldownContextType {
  state: DrilldownState;
  openDrilldown: (metric: string, filters?: DrilldownFilters, contextLabel?: string, initialTab?: string, headline?: DrilldownHeadline) => void;
  closeDrilldown: () => void;
  setFilters: (filters: DrilldownFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setGroupBy: (groupBy: string) => void;
  setLoading: (loading: boolean) => void;
  setMetricConfig: (config: {
    displayName: string;
    description: string;
    filterConfig: DrilldownFilterConfig[];
    groupings: string[];
    supportsTabs?: boolean;
    tabs?: DrilldownTab[];
  }) => void;
  setData: (data: {
    records: DrilldownRecord[];
    fields: DrilldownField[];
    displayName: string;
    totalCount: number;
    totalPages: number;
  }) => void;
  setBreakdown: (breakdown: unknown[]) => void;
  setError: (error: string | null) => void;
  setActiveTab: (tabKey: string) => void;
  setTabCounts: (counts: { [key: string]: number }) => void;
}

const initialState: DrilldownState = {
  isOpen: false,
  metric: null,
  displayName: null,
  description: null,
  filters: {},
  filterConfig: [],
  records: [],
  fields: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 25,
  totalPages: 1,
  groupBy: null,
  groupings: [],
  breakdown: null,
  contextLabel: null,
  headline: null,
  supportsTabs: false,
  tabs: [],
  activeTab: null,
  tabCounts: {},
  pendingInitialTab: null,
};

const DrilldownContext = createContext<DrilldownContextType | undefined>(undefined);

/**
 * Provides drilldown panel state for dashboard metric exploration.
 * Manages the open/close lifecycle, filters, pagination, tab navigation,
 * and data loading for the drilldown slide-out panel.
 */
export function DrilldownProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DrilldownState>(initialState);

  const openDrilldown = useCallback((metric: string, filters: DrilldownFilters = {}, contextLabel?: string, initialTab?: string, headline?: DrilldownHeadline) => {
    setState(_prev => ({
      ...initialState,
      isOpen: true,
      metric,
      filters,
      contextLabel: contextLabel || null,
      headline: headline || null,
      pendingInitialTab: initialTab || null,
      loading: true,
    }));
  }, []);

  const closeDrilldown = useCallback(() => {
    setState(initialState);
  }, []);

  const setFilters = useCallback((filters: DrilldownFilters) => {
    setState(prev => ({
      ...prev,
      filters,
      currentPage: 1,
      loading: true,
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page,
      loading: true,
    }));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setState(prev => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      loading: true,
    }));
  }, []);

  const setGroupBy = useCallback((groupBy: string) => {
    setState(prev => ({
      ...prev,
      groupBy,
      loading: true,
    }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, loading }));
  }, []);

  const setMetricConfig = useCallback((config: {
    displayName: string;
    description: string;
    filterConfig: DrilldownFilterConfig[];
    groupings: string[];
    supportsTabs?: boolean;
    tabs?: DrilldownTab[];
  }) => {
    setState(prev => {
      const tabs = config.tabs || [];
      // CR-4791: respect initialTab from the pie-slice click so the
      // drill-down opens on the matching tab instead of always COMPLETE.
      const initialIsValid = prev.pendingInitialTab
        && tabs.some(t => t.key === prev.pendingInitialTab);
      return {
        ...prev,
        displayName: config.displayName,
        description: config.description,
        filterConfig: config.filterConfig,
        groupings: config.groupings,
        supportsTabs: config.supportsTabs || false,
        tabs,
        activeTab: initialIsValid
          ? prev.pendingInitialTab
          : (tabs.length > 0 ? tabs[0].key : null),
        pendingInitialTab: null,
      };
    });
  }, []);

  const setData = useCallback((data: {
    records: DrilldownRecord[];
    fields: DrilldownField[];
    displayName: string;
    totalCount: number;
    totalPages: number;
  }) => {
    setState(prev => ({
      ...prev,
      records: data.records,
      fields: data.fields,
      displayName: data.displayName,
      totalCount: data.totalCount,
      totalPages: data.totalPages,
      loading: false,
      error: null,
    }));
  }, []);

  const setBreakdown = useCallback((breakdown: unknown[]) => {
    setState(prev => ({
      ...prev,
      breakdown,
      loading: false,
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error,
      loading: false,
    }));
  }, []);

  const setActiveTab = useCallback((tabKey: string) => {
    setState(prev => ({
      ...prev,
      activeTab: tabKey,
      currentPage: 1,
      loading: true,
    }));
  }, []);

  const setTabCounts = useCallback((counts: { [key: string]: number }) => {
    setState(prev => ({
      ...prev,
      tabCounts: counts,
    }));
  }, []);

  return (
    <DrilldownContext.Provider
      value={{
        state,
        openDrilldown,
        closeDrilldown,
        setFilters,
        setPage,
        setPageSize,
        setGroupBy,
        setLoading,
        setMetricConfig,
        setData,
        setBreakdown,
        setError,
        setActiveTab,
        setTabCounts,
      }}
    >
      {children}
    </DrilldownContext.Provider>
  );
}

/**
 * Accesses the drilldown panel state and actions for metric exploration.
 * Must be used within a {@link DrilldownProvider}.
 *
 * @returns Drilldown state with open/close actions, filter/pagination setters, and tab controls
 */
export function useDrilldown() {
  const context = useContext(DrilldownContext);
  if (context === undefined) {
    throw new Error('useDrilldown must be used within a DrilldownProvider');
  }
  return context;
}
